import moment from 'moment'
import QRequest from '@/api'

let idSeed = 1000

function nextId (prefix) {
  idSeed += 1
  return `${prefix}_${idSeed}`
}

function wait (data, message = 'success') {
  const payload = {
    code: 200,
    success: true,
    message,
    data
  }
  return new Promise(resolve => {
    setTimeout(() => resolve(JSON.parse(JSON.stringify(payload))), 180)
  })
}

function formatFileSize (size = 0) {
  if (size < 1024) {
    return `${size || 128}KB`
  }
  return `${(size / 1024).toFixed(2)}MB`
}

function createSvgUrl (text, background = '#E8F7F1', color = '#0BB78E') {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="320" height="220">
      <rect width="100%" height="100%" rx="16" ry="16" fill="${background}" />
      <rect x="24" y="24" width="272" height="172" rx="12" ry="12" fill="#ffffff" stroke="${color}" stroke-width="4" />
      <text x="160" y="118" text-anchor="middle" font-size="22" font-family="Arial, sans-serif" fill="${color}">${text}</text>
    </svg>
  `
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
}

function createTextDownloadUrl (content) {
  return `data:text/plain;charset=utf-8,${encodeURIComponent(content)}`
}

function pageList (list, pageNum = 1, pageSize = 10) {
  const start = (pageNum - 1) * pageSize
  const end = start + pageSize
  return {
    records: list.slice(start, end),
    total: list.length,
    pageNum,
    pageSize
  }
}

function getPreviewUrl (file, fallbackText) {
  if (file && file.raw && window.URL && window.URL.createObjectURL) {
    return window.URL.createObjectURL(file.raw)
  }
  if (file instanceof File && window.URL && window.URL.createObjectURL) {
    return window.URL.createObjectURL(file)
  }
  return createSvgUrl(fallbackText || '素材图')
}

function getFileName (file, defaultName) {
  return (file && (file.name || (file.raw && file.raw.name))) || defaultName
}

function getFileSize (file) {
  return formatFileSize((file && (file.size || (file.raw && file.raw.size))) || 256)
}

function normalizeTreeNodes (list = []) {
  return (list || []).map(item => ({
    id: String(item.id),
    label: item.folderName,
    folderPath: item.folderPath || '',
    boolTemp: item.boolTemp,
    children: normalizeTreeNodes(item.children || []),
    parentId: item.parentId
  }))
}

function normalizePageData (data = {}, records = []) {
  return {
    records,
    total: data.total || 0,
    pageNum: data.pageNum || 1,
    pageSize: data.pageSize || records.length || 10
  }
}

function normalizeFileRecord (item = {}) {
  return {
    id: item.dataId,
    dataId: item.dataId,
    fileName: item.fileName,
    url: item.address || item.url,
    filePath: item.filePath,
    starLevel: Number(item.starNum || 0),
    launchTime: item.creationDate || item.lastUpdateDate || '',
    uploadTime: item.creationDate || item.lastUpdateDate || '',
    uploader: item.lastUpdatedBy || item.createdBy || '',
    size: item.fileSize,
    resolution: item.imageWidth && item.imageHeight ? `${item.imageWidth} x ${item.imageHeight}` : '',
    folderId: item.folderId,
    folderType: item.folderType,
    tempId: item.tempId,
    modelName: item.model || '',
    model: item.model || '',
    skuCode: item.skuCode || ''
  }
}

function normalizeActivityUploadRecord (item = {}) {
  return {
    id: item.id || item.dataId || item.fileId || nextId('uploadRecord'),
    uploadYear: item.uploadYear || item.year || item.generateYear || item.imgYear || '',
    uploadMonth: item.uploadMonth || item.month || item.generateMonth || item.imgMonth || '',
    folderName: item.folderName || item.uploadFolderName || item.parentFolderName || '',
    imageName: item.imageName || item.imgName || item.pictureName || item.fileName || item.name || '',
    generateStatus: item.generateStatus || item.status || item.taskStatus || item.importStatus || '',
    failReason: item.errorReason || item.errorMsg || item.errorMessage || item.reason || '',
    uploader: item.uploader || item.importer || item.createUserName || item.createdBy || item.lastUpdatedBy || '',
    createdDateName: item.createdDateName || item.importTime || item.createTime || item.creationDate || item.lastUpdateTime || '',
    raw: item
  }
}

function normalizeStoreOptions (list = []) {
  return (list || []).map(item => ({
    value: item.storeCode || item.id || item.storeId || '',
    label: item.storeName || item.name || item.shortName || ''
  })).filter(item => item.value)
}

function normalizeModelOptions (list = []) {
  return (list || []).map(item => {
    const modelName = item && item.modelName ? String(item.modelName) : ''
    return {
      value: modelName,
      label: modelName
    }
  }).filter(item => item.value)
}

function normalizeFontOptions (list = []) {
  return (list || []).map(item => {
    const fontName = item && item.fontName ? String(item.fontName) : ''
    return {
      value: fontName,
      label: fontName
    }
  }).filter(item => item.value)
}

function getFirstLeafIdFromTree (tree = []) {
  if (!tree.length) return ''
  const current = tree[0]
  if (current.children && current.children.length) {
    return getFirstLeafIdFromTree(current.children)
  }
  return current.id
}

function parseRgbColor (color) {
  if (!color) {
    return { r: 51, g: 51, b: 51 }
  }
  if (color.indexOf('#') === 0) {
    const hex = color.replace('#', '')
    const fullHex = hex.length === 3 ? hex.split('').map(item => `${item}${item}`).join('') : hex
    return {
      r: parseInt(fullHex.slice(0, 2), 16),
      g: parseInt(fullHex.slice(2, 4), 16),
      b: parseInt(fullHex.slice(4, 6), 16)
    }
  }
  const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i)
  if (match) {
    return {
      r: Number(match[1]),
      g: Number(match[2]),
      b: Number(match[3])
    }
  }
  return { r: 51, g: 51, b: 51 }
}

function buildCopyGroupsFromDetailList (detailList = []) {
  return (detailList || []).map(item => ({
    id: item.id || '',
    uid: item.id || `${Date.now()}_${Math.random()}`,
    title: item.templateName || '',
    fields: [
      {
        key: 'price',
        label: '到手价',
        x: item.handPriceXPos,
        y: item.handPriceYPos,
        fontFamily: item.handPriceFont || 'Microsoft YaHei',
        fontSize: item.handPriceFontSize || 24,
        fontColor: `rgb(${item.handPriceFontR || 51}, ${item.handPriceFontG || 51}, ${item.handPriceFontB || 51})`,
        charLimit: item.handPriceCharLimit
      },
      {
        key: 'rankOrMinute',
        label: '前*名/前*分钟',
        x: item.rankNameXPos,
        y: item.rankNameYPos,
        fontFamily: item.rankNameFont || 'Microsoft YaHei',
        fontSize: item.rankNameFontSize || 24,
        fontColor: `rgb(${item.rankNameFontR || 51}, ${item.rankNameFontG || 51}, ${item.rankNameFontB || 51})`,
        charLimit: item.rankNameCharLimit
      },
      {
        key: 'discountOrGift',
        label: '立减/送',
        x: item.discountXPos,
        y: item.discountYPos,
        fontFamily: item.discountFont || 'Microsoft YaHei',
        fontSize: item.discountFontSize || 24,
        fontColor: `rgb(${item.discountFontR || 51}, ${item.discountFontG || 51}, ${item.discountFontB || 51})`,
        charLimit: item.discountCharLimit
      }
    ]
  }))
}

function normalizeTemplateRecord (item = {}) {
  return {
    id: item.id,
    storeId: item.storeCode,
    storeName: item.storeName,
    templateName: item.templateName,
    resolutionWidth: item.imageWidth,
    resolutionHeight: item.imageHeight,
    resolutionText: `${item.imageWidth || 0} - ${item.imageHeight || 0} px`,
    previewUrl: item.previewImageUrl,
    baseImageUrl: item.baseImageUrl,
    groupNames: item.detailNum,
    updater: item.updatedBy,
    updateTime: item.updatedDate ? moment(item.updatedDate).format('YYYY-MM-DD HH:mm:ss') : '',
    stickers: item.stickers,
    copyGroups: buildCopyGroupsFromDetailList(item.detailList || [])
  }
}

function buildTemplateSavePayload (payload = {}) {
  const detailList = (payload.copyGroups || []).map((group, index) => {
    const priceField = (group.fields || []).find(item => item.key === 'price') || {}
    const rankField = (group.fields || []).find(item => item.key === 'rankOrMinute') || {}
    const discountField = (group.fields || []).find(item => item.key === 'discountOrGift') || {}
    const priceColor = parseRgbColor(priceField.fontColor)
    const rankColor = parseRgbColor(rankField.fontColor)
    const discountColor = parseRgbColor(discountField.fontColor)
    return {
      id: group.id || '',
      sortNo: index + 1,
      templateId: payload.id || '',
      templateName: `文案组${index + 1}`,
      handPriceXPos: priceField.x,
      handPriceYPos: priceField.y,
      handPriceFont: priceField.fontFamily || '',
      handPriceFontSize: priceField.fontSize,
      handPriceFontR: priceColor.r,
      handPriceFontG: priceColor.g,
      handPriceFontB: priceColor.b,
      handPriceCharLimit: priceField.charLimit,
      rankNameXPos: rankField.x,
      rankNameYPos: rankField.y,
      rankNameFont: rankField.fontFamily || '',
      rankNameFontSize: rankField.fontSize,
      rankNameFontR: rankColor.r,
      rankNameFontG: rankColor.g,
      rankNameFontB: rankColor.b,
      rankNameCharLimit: rankField.charLimit,
      discountXPos: discountField.x,
      discountYPos: discountField.y,
      discountFont: discountField.fontFamily || '',
      discountFontSize: discountField.fontSize,
      discountFontR: discountColor.r,
      discountFontG: discountColor.g,
      discountFontB: discountColor.b,
      discountCharLimit: discountField.charLimit
    }
  })
  return {
    id: payload.id || '',
    storeCode: payload.storeId,
    storeName: payload.storeName,
    templateName: payload.templateName,
    imageWidth: payload.resolutionWidth,
    imageHeight: payload.resolutionHeight,
    previewImageUrl: payload.previewUrl,
    baseImageUrl: payload.previewUrl,
    stickers: payload.stickers,
    detailList
  }
}

function uploadAppFile (file) {
  if (!file) {
    return Promise.resolve('')
  }
  const formData = new FormData()
  formData.append('file', file)
  formData.append('appCode', 'ec-pc-api')
  return fetch('https://upload.quanyou.com.cn/gw/qu-file-upload-api/upload/app/uploadFile', {
    method: 'POST',
    body: formData,
    mode: 'cors'
  }).then(response => response.json()).then(res => {
    if (res.success && res.data && res.data.fileUrl) {
      return res.data.fileUrl
    }
    throw new Error(res.message || '上传失败')
  })
}

const aiFolders = [
  {
    id: 'ai-folder-1',
    label: '首页素材',
    children: [
      { id: 'ai-folder-1-1', label: '春夏活动', children: [] },
      { id: 'ai-folder-1-2', label: '大促主图', children: [] }
    ]
  },
  {
    id: 'ai-folder-2',
    label: '详情页素材',
    children: [
      { id: 'ai-folder-2-1', label: '卖点长图', children: [] },
      { id: 'ai-folder-2-2', label: '场景图', children: [] }
    ]
  }
]

const activityFolders = [
  {
    id: 'activity-folder-1',
    label: '首页活动图',
    children: [
      { id: 'activity-folder-1-1', label: '秒杀专区', children: [] },
      { id: 'activity-folder-1-2', label: '会员专区', children: [] }
    ]
  },
  {
    id: 'activity-folder-2',
    label: '频道活动图',
    children: [
      { id: 'activity-folder-2-1', label: '新品频道', children: [] },
      { id: 'activity-folder-2-2', label: '清仓频道', children: [] }
    ]
  }
]

const modelOptions = [
  { value: 'M001', label: '极简卧室模型' },
  { value: 'M002', label: '奶油客厅模型' },
  { value: 'M003', label: '北欧餐厅模型' },
  { value: 'M004', label: '工业风书房模型' },
  { value: 'M005', label: '法式阳台模型' }
]

const storeOptions = [
  { value: 'S001', label: '天猫旗舰店' },
  { value: 'S002', label: '京东自营店' },
  { value: 'S003', label: '拼多多官方店' }
]

const templateOptions = [
  {
    value: 'T001',
    label: '店铺活动图导入模板A',
    resolution: '1242 x 720',
    storeIds: ['S001', 'S002']
  },
  {
    value: 'T002',
    label: '店铺活动图导入模板B',
    resolution: '750 x 960',
    storeIds: ['S001', 'S003']
  }
]

const fontOptions = []

function createTemplateConfig (params = {}) {
  const {
    id = nextId('template'),
    storeId,
    storeName,
    templateName,
    resolutionWidth,
    resolutionHeight,
    updater,
    updateDays,
    previewText,
    copyGroups,
    stickers = []
  } = params
  return {
    id,
    storeId,
    storeName,
    templateName,
    resolutionWidth,
    resolutionHeight,
    resolutionText: `${resolutionWidth} - ${resolutionHeight} px`,
    previewUrl: createSvgUrl(previewText || templateName, '#FFF4EA', '#E67E22'),
    groupNames: copyGroups.map((item, index) => `文案组${index + 1}`).join(' / '),
    updater,
    updateTime: moment().subtract(updateDays, 'days').format('YYYY-MM-DD HH:mm:ss'),
    stickers,
    copyGroups
  }
}

function createCopyGroups (prefix) {
  return [
    {
      uid: nextId('group'),
      title: `${prefix}文案组1`,
      fields: [
        { key: 'price', label: '到手价', x: 120, y: 80, fontFamily: 'Microsoft YaHei', fontSize: 28, fontColor: 'rgba(51, 51, 51, 1)', charLimit: 12 },
        { key: 'rankOrMinute', label: '前*名/前*分钟', x: 120, y: 130, fontFamily: 'Microsoft YaHei', fontSize: 24, fontColor: 'rgba(51, 51, 51, 1)', charLimit: 10 },
        { key: 'discountOrGift', label: '立减/送', x: 120, y: 180, fontFamily: 'Microsoft YaHei', fontSize: 26, fontColor: 'rgba(51, 51, 51, 1)', charLimit: 8 }
      ]
    }
  ]
}

function createAiMaterial (folderId, fileName, starLevel, days, uploader, modelName) {
  return {
    id: nextId('ai'),
    folderId,
    fileName,
    starLevel,
    launchTime: moment().subtract(days, 'days').format('YYYY-MM-DD HH:mm:ss'),
    uploader,
    size: formatFileSize(320 + days * 56),
    url: createSvgUrl(fileName, '#F6FBFF', '#409EFF'),
    modelName
  }
}

function createActivityMaterial (folderId, fileName, days, uploader, resolution = '1242 x 720', storeName = '天猫旗舰店', templateName = '店铺活动图导入模板A') {
  return {
    id: nextId('activity'),
    folderId,
    fileName,
    uploadTime: moment().subtract(days, 'days').format('YYYY-MM-DD HH:mm:ss'),
    uploader,
    size: formatFileSize(280 + days * 64),
    url: createSvgUrl(fileName, '#FFF8EA', '#E6A23C'),
    resolution,
    storeName,
    templateName
  }
}

const state = {
  aiMaterials: [
    createAiMaterial('ai-folder-1-1', '春夏卧室主图.jpg', 4, 1, '张三', '极简卧室模型'),
    createAiMaterial('ai-folder-1-1', '春夏客厅陈列图.png', 5, 2, '李四', '奶油客厅模型'),
    createAiMaterial('ai-folder-1-2', '618爆款头图.jpg', 3, 3, '王五', '北欧餐厅模型'),
    createAiMaterial('ai-folder-2-1', '卖点长图-床垫.png', 2, 4, '赵六', '工业风书房模型'),
    createAiMaterial('ai-folder-2-2', '场景图-阳台椅.jpg', 5, 5, '孙七', '法式阳台模型')
  ],
  activityMaterials: [
    createActivityMaterial('activity-folder-1-1', '秒杀专区底图.jpg', 1, '张三', '1242 x 720', '天猫旗舰店', '店铺活动图导入模板A'),
    createActivityMaterial('activity-folder-1-1', '秒杀专区活动图.png', 2, '李四', '1242 x 720', '京东自营店', '店铺活动图导入模板A'),
    createActivityMaterial('activity-folder-1-2', '会员专区头图.jpg', 3, '王五', '750 x 960', '拼多多官方店', '店铺活动图导入模板B'),
    createActivityMaterial('activity-folder-2-1', '新品频道活动图.jpg', 4, '赵六', '1242 x 720', '天猫旗舰店', '店铺活动图导入模板A'),
    createActivityMaterial('activity-folder-2-2', '清仓频道底图.png', 5, '孙七', '750 x 960', '拼多多官方店', '店铺活动图导入模板B')
  ],
  activityUploadTasks: [
    {
      id: nextId('task'),
      fileName: '店铺活动图导入_2026-04-29.xlsx',
      taskType: '店铺活动图上传',
      importer: '张三',
      status: '导入成功',
      importTime: moment().subtract(1, 'days').format('YYYY-MM-DD HH:mm:ss'),
      downloadUrl: createTextDownloadUrl('mock excel content')
    },
    {
      id: nextId('task'),
      fileName: '店铺活动图导入_2026-04-28.xlsx',
      taskType: '店铺活动图上传',
      importer: '李四',
      status: '处理中',
      importTime: moment().subtract(2, 'days').format('YYYY-MM-DD HH:mm:ss'),
      downloadUrl: createTextDownloadUrl('mock excel content')
    }
  ],
  templateConfigs: [
    createTemplateConfig({
      storeId: 'S001',
      storeName: '天猫旗舰店',
      templateName: '秒杀专区模板A',
      resolutionWidth: 1242,
      resolutionHeight: 720,
      updater: '张三',
      updateDays: 1,
      previewText: '秒杀模板A',
      copyGroups: createCopyGroups('秒杀')
    }),
    createTemplateConfig({
      storeId: 'S002',
      storeName: '京东自营店',
      templateName: '会员专区模板B',
      resolutionWidth: 1242,
      resolutionHeight: 720,
      updater: '李四',
      updateDays: 2,
      previewText: '会员模板B',
      copyGroups: createCopyGroups('会员')
    }),
    createTemplateConfig({
      storeId: 'S003',
      storeName: '拼多多官方店',
      templateName: '清仓频道模板C',
      resolutionWidth: 750,
      resolutionHeight: 960,
      updater: '王五',
      updateDays: 3,
      previewText: '清仓模板C',
      copyGroups: createCopyGroups('清仓')
    })
  ],
  searchLogs: {
    1: ['春夏活动', '大促主图', '场景图'],
    2: ['秒杀专区', '会员专区', '新品频道']
  }
}

function getFirstLeafId (tree = []) {
  if (!tree.length) return ''
  const current = tree[0]
  if (current.children && current.children.length) {
    return getFirstLeafId(current.children)
  }
  return current.id
}

function filterByName (list, fileName) {
  if (!fileName) return list
  const keyword = fileName.trim().toLowerCase()
  return list.filter(item => item.fileName.toLowerCase().includes(keyword))
}

const mockService = {
  uploadAppFile,
  getAiFolders () {
    return wait({
      tree: aiFolders,
      defaultFolderId: getFirstLeafId(aiFolders)
    })
  },
  getActivityFolders () {
    return wait({
      tree: activityFolders,
      defaultFolderId: getFirstLeafId(activityFolders)
    })
  },
  getAiMaterialList (params = {}) {
    const { folderId, fileName = '', pageNum = 1, pageSize = 10 } = params
    const result = state.aiMaterials.filter(item => item.folderId === folderId)
    const filtered = filterByName(result, fileName)
    return wait(pageList(filtered, pageNum, pageSize))
  },
  getActivityMaterialList (params = {}) {
    const { folderId, fileName = '', pageNum = 1, pageSize = 10 } = params
    const result = state.activityMaterials.filter(item => item.folderId === folderId)
    const filtered = filterByName(result, fileName)
    return wait(pageList(filtered, pageNum, pageSize))
  },
  searchModelOptions (keyword = '') {
    const query = keyword.trim().toLowerCase()
    const result = !query
      ? modelOptions
      : modelOptions.filter(item => item.label.toLowerCase().includes(query) || item.value.toLowerCase().includes(query))
    return wait(result)
  },
  uploadAiMaterial (params = {}) {
    const { folderId, uploadMode = 'add', modelId = '', modelName = '', files = [] } = params
    const file = files[0]
    const fileName = getFileName(file, `AI素材_${Date.now()}.png`)
    const row = {
      id: nextId('ai'),
      folderId,
      fileName,
      starLevel: 0,
      launchTime: moment().format('YYYY-MM-DD HH:mm:ss'),
      uploader: '当前用户',
      size: getFileSize(file),
      url: getPreviewUrl(file, fileName),
      modelId,
      modelName
    }
    if (uploadMode === 'replace') {
      const index = state.aiMaterials.findIndex(item => item.folderId === folderId && item.fileName === fileName)
      if (index > -1) {
        state.aiMaterials.splice(index, 1, row)
      } else {
        state.aiMaterials.unshift(row)
      }
    } else {
      state.aiMaterials.unshift(row)
    }
    return wait(row, '上传成功')
  },
  scoreAiMaterial (params = {}) {
    const { id, starLevel = 0 } = params
    const target = state.aiMaterials.find(item => item.id === id)
    if (target) {
      target.starLevel = starLevel
    }
    return wait(target || {}, '评分成功')
  },
  deleteAiMaterials (params = {}) {
    const ids = params.ids || []
    state.aiMaterials = state.aiMaterials.filter(item => !ids.includes(item.id))
    return wait(true, '删除成功')
  },
  downloadAiMaterials () {
    return wait(true, '下载成功')
  },
  uploadActivityBaseImage (params = {}) {
    const {
      folderId,
      files = [],
      uploadMode = 'add',
      resolution = '',
      storeId = '',
      storeName = '',
      templateId = '',
      templateName = ''
    } = params
    const file = files[0]
    const fileName = getFileName(file, `活动图_${Date.now()}.png`)
    const row = {
      id: nextId('activity'),
      folderId,
      fileName,
      uploadTime: moment().format('YYYY-MM-DD HH:mm:ss'),
      uploader: '当前用户',
      size: getFileSize(file),
      url: getPreviewUrl(file, fileName),
      resolution,
      storeId,
      storeName,
      templateId,
      templateName
    }
    if (uploadMode === 'replace') {
      const index = state.activityMaterials.findIndex(item => item.folderId === folderId && item.fileName === fileName)
      if (index > -1) {
        state.activityMaterials.splice(index, 1, row)
      } else {
        state.activityMaterials.unshift(row)
      }
    } else {
      state.activityMaterials.unshift(row)
    }
    return wait(row, '上传成功')
  },
  deleteActivityMaterials (params = {}) {
    const ids = params.ids || []
    state.activityMaterials = state.activityMaterials.filter(item => !ids.includes(item.id))
    return wait(true, '删除成功')
  },
  downloadActivityMaterials () {
    return wait(true, '下载成功')
  },
  getStoreOptions () {
    return wait(storeOptions)
  },
  getTemplateOptions () {
    return wait(templateOptions)
  },
  getActivityUploadTaskList (params = {}) {
    const { dateRange = [], pageNum = 1, pageSize = 10 } = params
    let result = state.activityUploadTasks.slice()
    if (dateRange && dateRange.length === 2) {
      const start = moment(dateRange[0]).startOf('day').valueOf()
      const end = moment(dateRange[1]).endOf('day').valueOf()
      result = result.filter(item => {
        const current = moment(item.importTime).valueOf()
        return current >= start && current <= end
      })
    }
    result.sort((a, b) => moment(b.importTime).valueOf() - moment(a.importTime).valueOf())
    return wait(pageList(result, pageNum, pageSize))
  },
  uploadActivityTask (params = {}) {
    const { storeId = '', file, stickerUrl = '' } = params
    const formData = new FormData()
    const targetFile = file && (file.raw || file)
    if (targetFile) {
      formData.append('file', targetFile)
    }
    formData.append('stickerUrl', stickerUrl)
    return QRequest.newProduct.storeDecorationActivityUpload({
      data: formData,
      params: {
        storeCode: storeId
      },
      options: {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    }).catch(() => {
      const fileName = getFileName(file, `店铺活动图导入_${Date.now()}.xlsx`)
      const row = {
        id: nextId('task'),
        fileName,
        taskType: '店铺活动图上传',
        importer: '当前用户',
        status: '导入成功',
        importTime: moment().format('YYYY-MM-DD HH:mm:ss'),
        downloadUrl: file && file.raw && window.URL && window.URL.createObjectURL
          ? window.URL.createObjectURL(file.raw)
          : createTextDownloadUrl('mock upload task file')
      }
      state.activityUploadTasks.unshift(row)
      return wait(row, '上传成功')
    })
  },
  downloadActivityTemplate () {
    return wait({
      fileName: '店铺活动图上传模板.csv',
      url: createTextDownloadUrl('店铺,模板,活动图链接')
    })
  },
  getStoreDecorationTemplateList (params = {}) {
    const { storeIds = [], pageNum = 1, pageSize = 10 } = params
    let result = state.templateConfigs.slice()
    if (storeIds.length) {
      result = result.filter(item => storeIds.includes(item.storeId))
    }
    result.sort((a, b) => moment(b.updateTime).valueOf() - moment(a.updateTime).valueOf())
    return wait(pageList(result, pageNum, pageSize))
  },
  saveStoreDecorationTemplate (params = {}) {
    const {
      id = '',
      storeId = '',
      storeName = '',
      templateName = '',
      resolutionWidth = '',
      resolutionHeight = '',
      previewUrl = '',
      stickers = [],
      copyGroups = []
    } = params
    const row = {
      id: id || nextId('template'),
      storeId,
      storeName,
      templateName,
      resolutionWidth,
      resolutionHeight,
      resolutionText: `${resolutionWidth} - ${resolutionHeight} px`,
      previewUrl: previewUrl || createSvgUrl(templateName, '#FFF4EA', '#E67E22'),
      groupNames: copyGroups.map((item, index) => `文案组${index + 1}`).join(' / '),
      updater: '当前用户',
      updateTime: moment().format('YYYY-MM-DD HH:mm:ss'),
      stickers: JSON.parse(JSON.stringify(stickers)),
      copyGroups: JSON.parse(JSON.stringify(copyGroups))
    }
    const index = state.templateConfigs.findIndex(item => item.id === row.id)
    if (index > -1) {
      state.templateConfigs.splice(index, 1, row)
    } else {
      state.templateConfigs.unshift(row)
    }
    return wait(row, id ? '编辑成功' : '新增成功')
  },
  deleteStoreDecorationTemplate (params = {}) {
    const { id = '' } = params
    state.templateConfigs = state.templateConfigs.filter(item => item.id !== id)
    return wait(true, '删除成功')
  }
}

const serviceOverrides = {
  saveStoreDecorationSearchLog (params = {}) {
    const { searchType = '1', searchKey = '' } = params
    const key = String(searchType)
    const value = (searchKey || '').trim()
    if (!value) {
      return wait(true, '保存成功')
    }
    return QRequest.newProduct.storeDecorationSaveSearchLog({
      fragment: [key, encodeURIComponent(value)]
    }).then(res => {
      const currentLogs = state.searchLogs[key] || []
      state.searchLogs[key] = [value, ...currentLogs.filter(item => item !== value)].slice(0, 20)
      return res
    }).catch(() => {
      const currentLogs = state.searchLogs[key] || []
      state.searchLogs[key] = [value, ...currentLogs.filter(item => item !== value)].slice(0, 20)
      return wait(true, '保存成功')
    })
  },
  getStoreDecorationSearchLogOptions (params = {}) {
    const { searchType = '1' } = params
    const key = String(searchType)
    return QRequest.common.reportsData({
      data: {
        pageSize: 50,
        pageNum: 1,
        reportCode: 'queryStoreDecorationSearchLog',
        param: {
          searchType: key
        },
        queryMatch: [],
        orderList: [],
        hasCount: 'N',
        groupAttr: ''
      }
    }).then(res => {
      const list = ((res.data && res.data.list) || []).map(item => {
        const value = item.searchKey || item.searchValue || item.keyword || item.name || ''
        return {
          value,
          label: value
        }
      }).filter(item => item.value)
      return {
        code: res.code,
        success: res.success,
        message: res.message,
        data: list
      }
    }).catch(() => {
      const list = (state.searchLogs[key] || []).map(item => ({
        value: item,
        label: item
      }))
      return wait(list)
    })
  },
  getAiFolders () {
    return QRequest.newProduct.storeDecorationFolderTree({ fragment: ['1'], data: {} }).then(res => {
      const tree = normalizeTreeNodes(res.data || [])
      return {
        code: res.code,
        success: res.success,
        message: res.message,
        data: {
          tree,
          defaultFolderId: getFirstLeafIdFromTree(tree)
        }
      }
    }).catch(() => {
      return wait({
        tree: aiFolders,
        defaultFolderId: getFirstLeafId(aiFolders)
      })
    })
  },
  getActivityFolders () {
    return QRequest.newProduct.storeDecorationFolderTree({ fragment: ['2'], data: {} }).then(res => {
      const tree = normalizeTreeNodes(res.data || [])
      return {
        code: res.code,
        success: res.success,
        message: res.message,
        data: {
          tree,
          defaultFolderId: getFirstLeafIdFromTree(tree)
        }
      }
    }).catch(() => {
      return wait({
        tree: activityFolders,
        defaultFolderId: getFirstLeafId(activityFolders)
      })
    })
  },
  getAiMaterialList (params = {}) {
    const { folderId, fileName = '', pageNum = 1, pageSize = 10 } = params
    return QRequest.newProduct.storeDecorationAiMaterialList({
      data: {
        pageNum,
        pageSize,
        filterData: {
          fileName,
          folderId: Number(folderId),
          folderType: '1'
        }
      }
    }).then(res => {
      const records = (res.data.records || []).map(item => normalizeFileRecord(item))
      return {
        code: res.code,
        success: res.success,
        message: res.message,
        data: normalizePageData(res.data, records)
      }
    }).catch(() => {
      const result = state.aiMaterials.filter(item => item.folderId === folderId)
      const filtered = filterByName(result, fileName)
      return wait(pageList(filtered, pageNum, pageSize))
    })
  },
  getActivityMaterialList (params = {}) {
    const { folderId, fileName = '', pageNum = 1, pageSize = 10 } = params
    return QRequest.newProduct.storeDecorationActivityMaterialList({
      data: {
        pageNum,
        pageSize,
        filterData: {
          fileName,
          folderId: Number(folderId),
          folderType: '2'
        }
      }
    }).then(res => {
      const records = (res.data.records || []).map(item => normalizeFileRecord(item))
      return {
        code: res.code,
        success: res.success,
        message: res.message,
        data: normalizePageData(res.data, records)
      }
    }).catch(() => {
      const result = state.activityMaterials.filter(item => item.folderId === folderId)
      const filtered = filterByName(result, fileName)
      return wait(pageList(filtered, pageNum, pageSize))
    })
  },
  searchModelOptions (keyword = '') {
    return QRequest.common.reportsData({
      data: {
        pageSize: 999,
        pageNum: 1,
        reportCode: 'productModelList',
        param: {},
        queryMatch: [],
        orderList: [],
        hasCount: 'N',
        groupAttr: ''
      }
    }).then(res => {
      let list = normalizeModelOptions((res.data && res.data.list) || [])
      if (keyword) {
        const query = keyword.trim().toLowerCase()
        list = list.filter(item => item.label.toLowerCase().includes(query) || item.value.toLowerCase().includes(query))
      }
      return {
        code: res.code,
        success: res.success,
        message: res.message,
        data: list
      }
    }).catch(() => {
      const query = keyword.trim().toLowerCase()
      const result = !query
        ? modelOptions
        : modelOptions.filter(item => item.label.toLowerCase().includes(query) || item.value.toLowerCase().includes(query))
      return wait(result)
    })
  },
  uploadAiMaterial (params = {}) {
    const { uploadMode = 'add', modelId = '', modelName = '', files = [] } = params
    const formData = new FormData()
    files.forEach(item => {
      const file = item.raw || item
      formData.append('files', file)
    })
    return QRequest.newProduct.storeDecorationAiMaterialUpload({
      data: formData,
      params: {
        productModel: modelName || modelId,
        uploadType: uploadMode
      },
      options: {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    }).catch(() => {
      const file = files[0]
      const fileName = getFileName(file, `AI素材_${Date.now()}.png`)
      const row = {
        id: nextId('ai'),
        folderId: params.folderId,
        fileName,
        starLevel: 0,
        launchTime: moment().format('YYYY-MM-DD HH:mm:ss'),
        uploader: '当前用户',
        size: getFileSize(file),
        url: getPreviewUrl(file, fileName),
        modelId,
        modelName
      }
      state.aiMaterials.unshift(row)
      return wait(row, '上传成功')
    })
  },
  scoreAiMaterial (params = {}) {
    const { id, starLevel = 0 } = params
    return QRequest.newProduct.storeDecorationAiMaterialScore({
      fragment: [id, starLevel]
    }).catch(() => {
      const target = state.aiMaterials.find(item => item.id === id)
      if (target) {
        target.starLevel = starLevel
      }
      return wait(target || {}, '评分成功')
    })
  },
  deleteAiMaterials (params = {}) {
    const ids = params.ids || []
    const dtoList = ids.map(id => ({ fileId: id, folderId: '', type: 'file' }))
    return QRequest.newProduct.storeDecorationAiMaterialDelete({
      data: dtoList
    }).catch(() => {
      state.aiMaterials = state.aiMaterials.filter(item => !ids.includes(item.id))
      return wait(true, '删除成功')
    })
  },
  downloadAiMaterials (params = {}) {
    const ids = params.ids || []
    const dtoList = ids.map(id => ({ fileId: id, folderId: '', path: '', folderName: '', type: 'file' }))
    return QRequest.newProduct.storeDecorationAiMaterialDownload({
      data: dtoList
    }).catch(() => wait(true, '下载成功'))
  },
  getStoreDecorationDownloadTaskInfo (taskId) {
    return QRequest.newProduct.storeDecorationDownloadTaskInfo({
      fragment: [taskId]
    }).catch(() => wait({
      taskStatus: '执行完成',
      filePath: '',
      taskDesc: ''
    }))
  },
  uploadActivityBaseImage (params = {}) {
    const { files = [], uploadMode = 'add', templateId = '' } = params
    const formData = new FormData()
    files.forEach(item => {
      const file = item.raw || item
      formData.append('files', file)
    })
    return QRequest.newProduct.storeDecorationActivityBaseUpload({
      data: formData,
      params: {
        tempId: templateId,
        uploadType: uploadMode
      },
      options: {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    }).catch(() => {
      const file = files[0]
      const fileName = getFileName(file, `活动图_${Date.now()}.png`)
      const row = {
        id: nextId('activity'),
        folderId: params.folderId,
        fileName,
        uploadTime: moment().format('YYYY-MM-DD HH:mm:ss'),
        uploader: '当前用户',
        size: getFileSize(file),
        url: getPreviewUrl(file, fileName),
        resolution: params.resolution,
        storeId: params.storeId,
        storeName: params.storeName,
        templateId: params.templateId,
        templateName: params.templateName
      }
      state.activityMaterials.unshift(row)
      return wait(row, '上传成功')
    })
  },
  deleteActivityMaterials (params = {}) {
    const ids = params.ids || []
    const dtoList = ids.map(id => ({ fileId: id, folderId: '', type: 'file' }))
    return QRequest.newProduct.storeDecorationActivityMaterialDelete({
      data: dtoList
    }).catch(() => {
      state.activityMaterials = state.activityMaterials.filter(item => !ids.includes(item.id))
      return wait(true, '删除成功')
    })
  },
  deleteStoreDecorationFolder (params = {}) {
    const dtoList = [{
      fileId: '',
      folderId: params.folderId,
      type: 'folder'
    }]
    return QRequest.newProduct.storeDecorationActivityMaterialDelete({
      data: dtoList
    }).catch(() => wait(true, '删除成功'))
  },
  downloadActivityMaterials (params = {}) {
    const ids = params.ids || []
    const dtoList = ids.map(id => ({ fileId: id, folderId: '', path: '', folderName: '', type: 'file' }))
    return QRequest.newProduct.storeDecorationActivityMaterialDownload({
      data: dtoList
    }).catch(() => wait(true, '下载成功'))
  },
  getStoreOptions () {
    return QRequest.common.reportsData({
      data: {
        pageSize: 999,
        pageNum: 1,
        reportCode: 'storeDecorationStoreList',
        param: {},
        queryMatch: [],
        orderList: [],
        hasCount: 'N',
        groupAttr: ''
      }
    }).then(res => {
      return {
        code: res.code,
        success: res.success,
        message: res.message,
        data: normalizeStoreOptions((res.data && res.data.list) || [])
      }
    }).catch(() => wait(storeOptions))
  },
  getFontOptions () {
    return QRequest.common.reportsData({
      data: {
        pageSize: 999,
        pageNum: 1,
        reportCode: 'storeDecorationFontList',
        param: {},
        queryMatch: [],
        orderList: [],
        hasCount: 'N',
        groupAttr: ''
      }
    }).then(res => {
      return {
        code: res.code,
        success: res.success,
        message: res.message,
        data: normalizeFontOptions((res.data && res.data.list) || [])
      }
    }).catch(() => wait(fontOptions))
  },
  getActivityUploadRecords (params = {}) {
    const { pageNum = 1, pageSize = 10 } = params
    return QRequest.common.reportsData({
      data: {
        pageSize,
        pageNum,
        reportCode: 'storeDecorationGenerateImgList',
        param: {},
        queryMatch: [],
        orderList: [],
        hasCount: 'Y',
        groupAttr: ''
      }
    }).then(res => {
      const records = ((res.data && res.data.list) || []).map(item => normalizeActivityUploadRecord(item))
      return {
        code: res.code,
        success: res.success,
        message: res.message,
        data: {
          records,
          total: (res.data && res.data.total) || 0,
          pageNum: (res.data && res.data.pageNum) || pageNum,
          pageSize: (res.data && res.data.pageSize) || pageSize
        }
      }
    }).catch(() => {
      const result = state.activityUploadTasks
        .map(item => normalizeActivityUploadRecord(item))
        .sort((a, b) => moment(b.uploadTime).valueOf() - moment(a.uploadTime).valueOf())
      return wait(pageList(result, pageNum, pageSize))
    })
  },
  getTemplateOptions (params = {}) {
    const {
      storeCode = '',
      storeName = '',
      templateName = ''
    } = params
    return QRequest.newProduct.storeDecorationTemplateConfigList({
      data: {
        pageNum: 1,
        pageSize: 100,
        filterData: {
          storeCode,
          storeName,
          templateName
        }
      }
    }).then(res => {
      const options = (res.data.records || []).map(item => ({
        value: item.id,
        label: `${item.storeName || ''}-${item.templateName || ''}`,
        resolution: item.imageWidth && item.imageHeight ? `${item.imageWidth} x ${item.imageHeight}` : '',
        storeId: item.storeCode || '',
        templateName: item.templateName || '',
        storeName: item.storeName || ''
      }))
      return {
        code: res.code,
        success: res.success,
        message: res.message,
        data: options
      }
    }).catch(() => {
      let list = templateOptions
      if (storeCode) {
        list = list.filter(item => item.storeId === storeCode || (item.storeIds || []).includes(storeCode))
      }
      return wait(list)
    })
  },
  getStoreDecorationTemplateList (params = {}) {
    const { storeIds = [], pageNum = 1, pageSize = 10 } = params
    return QRequest.newProduct.storeDecorationTemplateConfigList({
      data: {
        pageNum,
        pageSize,
        filterData: {
          storeCode: (storeIds || []).join(',')
        }
      }
    }).then(res => {
      const records = (res.data.records || []).map(item => normalizeTemplateRecord(item))
      return {
        code: res.code,
        success: res.success,
        message: res.message,
        data: normalizePageData(res.data, records)
      }
    }).catch(() => {
      let result = state.templateConfigs.slice()
      if (storeIds.length) {
        result = result.filter(item => storeIds.includes(item.storeId))
      }
      result.sort((a, b) => moment(b.updateTime).valueOf() - moment(a.updateTime).valueOf())
      return wait(pageList(result, pageNum, pageSize))
    })
  },
  getStoreDecorationTemplateDetail (params = {}) {
    const { id = '' } = params
    return QRequest.newProduct.storeDecorationTemplateConfigDetail({
      fragment: [id]
    }).then(res => {
      return {
        code: res.code,
        success: res.success,
        message: res.message,
        data: normalizeTemplateRecord(res.data || {})
      }
    }).catch(() => {
      const row = state.templateConfigs.find(item => item.id === id)
      return wait(row || {})
    })
  },
  saveStoreDecorationTemplate (params = {}) {
    const doUpload = params.previewFile ? uploadAppFile(params.previewFile) : Promise.resolve(params.previewUrl || '')
    return doUpload.then(fileUrl => {
      const requestData = buildTemplateSavePayload({
        ...params,
        previewUrl: fileUrl || params.previewUrl
      })
      const apiMethod = params.id ? QRequest.newProduct.storeDecorationTemplateConfigUpdate : QRequest.newProduct.storeDecorationTemplateConfigAdd
      return apiMethod({
        data: requestData
      })
    }).catch(() => {
      const row = {
        id: params.id || nextId('template'),
        storeId: params.storeId,
        storeName: params.storeName,
        templateName: params.templateName,
        resolutionWidth: params.resolutionWidth,
        resolutionHeight: params.resolutionHeight,
        resolutionText: `${params.resolutionWidth} - ${params.resolutionHeight} px`,
        previewUrl: params.previewUrl || createSvgUrl(params.templateName, '#FFF4EA', '#E67E22'),
        groupNames: (params.copyGroups || []).map((item, index) => `文案组${index + 1}`).join(' / '),
        stickers: JSON.parse(JSON.stringify(params.stickers || [])),
        updater: '当前用户',
        updateTime: moment().format('YYYY-MM-DD HH:mm:ss'),
        copyGroups: JSON.parse(JSON.stringify(params.copyGroups || []))
      }
      const index = state.templateConfigs.findIndex(item => item.id === row.id)
      if (index > -1) {
        state.templateConfigs.splice(index, 1, row)
      } else {
        state.templateConfigs.unshift(row)
      }
      return wait(row, params.id ? '编辑成功' : '新增成功')
    })
  },
  deleteStoreDecorationTemplate (params = {}) {
    const { id = '' } = params
    return QRequest.newProduct.storeDecorationTemplateConfigDelete({
      fragment: [id]
    }).catch(() => {
      state.templateConfigs = state.templateConfigs.filter(item => item.id !== id)
      return wait(true, '删除成功')
    })
  }
}

export default Object.assign(mockService, serviceOverrides)
